import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, extractApiError } from "../../lib/api";
import { ACTIVITY_TYPE } from "../../lib/enums";
import { ACTIVITY_TYPE_LABELS } from "../../lib/labels";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";

const TYPES = [
  ACTIVITY_TYPE.CALL,
  ACTIVITY_TYPE.EMAIL,
  ACTIVITY_TYPE.MEETING,
  ACTIVITY_TYPE.DISCUSSION,
  ACTIVITY_TYPE.OTHER,
];

const DEFAULT_TYPE = ACTIVITY_TYPE.CALL;

const PLACEHOLDER = {
  [ACTIVITY_TYPE.CALL]: "Was wurde besprochen?",
  [ACTIVITY_TYPE.EMAIL]: "Kurze Zusammenfassung der E-Mail …",
  [ACTIVITY_TYPE.MEETING]: "Termin, Zeitpunkt oder Follow-up festhalten …",
  [ACTIVITY_TYPE.DISCUSSION]: "Kernaussagen, Beschlüsse oder nächste Schritte …",
  [ACTIVITY_TYPE.OTHER]: "Kurze Zusammenfassung der Aktivität …",
};

export default function AddActivityDialog({ open, onOpenChange, relationPayload, onCreated }) {
  const [type, setType] = useState(DEFAULT_TYPE);
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const placeholder = useMemo(() => PLACEHOLDER[type] || PLACEHOLDER[DEFAULT_TYPE], [type]);

  const reset = () => {
    setType(DEFAULT_TYPE);
    setDescription("");
  };

  const handleOpenChange = (next) => {
    if (saving) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const trimmed = (description || "").trim();
    if (!trimmed) {
      toast.error("Bitte eine Beschreibung eingeben.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/activities", { type, description: trimmed, ...(relationPayload || {}) });
      toast.success("Aktivität hinzugefügt");
      handleOpenChange(false);
      if (onCreated) onCreated();
    } catch (err) {
      toast.error(extractApiError(err, "Aktivität konnte nicht gespeichert werden."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Aktivität hinzufügen</DialogTitle>
          <DialogDescription>Notiere eine Aktivität, die in der Timeline angezeigt wird.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Typ</Label>
            <Select value={type} onValueChange={setType} disabled={saving}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{ACTIVITY_TYPE_LABELS[t]?.de || t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Beschreibung</Label>
            <Textarea
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={placeholder}
              disabled={saving}
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={saving}>{saving ? "Speichern …" : "Speichern"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
